/**
 * GravityForms tools.
 *
 * @since 0.1.0
 */

// console.log('Site:gravityforms');

/*
The populateForm() methods lets you dynamically fill out Gravityforms using Javascript, however in order for it to work
there need to be mappings from the form & field IDs to the dynamic parameter name.  This mapping is written to the
page by PHP and loaded here.  Since the script to setup the mappings runs before this code does, it's stored in a
variable until ready to be loaded `window.queuedFormFieldMaps`.

The key is the form id, the value contains: id (field), name (dynamic mapping parameter in the admin)
Currently hidden and simple text field types are supported

Example script:
Form ID is 7

<script>
window.queuedFormFieldMaps = window.queuedFormFieldMaps || {};
window.queuedFormFieldMaps[7] = {
  {
    first_name : {
      id : 2,
    },
    last_name : {
      id : 3,
    },
    source : {
      id : 21,
    },
  },
};
</script>
*/

let formFieldMaps = {};

// Load queued data
if (window.queuedFormFieldMaps && typeof window.queuedFormFieldMaps === 'object') {
  formFieldMaps = window.queuedFormFieldMaps;
}

// console.log('formFieldMaps', formFieldMaps);


/**
 * Sets the field map for a form.
 *
 * @since 0.1.0
 *
 * @param {int}    formId The form ID.
 * @param {object} data   The data.
 */
export function setFieldMap(formId, data) {
  // console.log('Site:gravityforms:setFieldMap', formId, data);

  formFieldMaps[formId] = data;
}


/**
 * Get the field map for the specified form.
 *
 * @since 0.1.0
 *
 * @param {int} formId The form ID.
 *
 * @throw  Error
 *
 * @return {object} object Form data.
 */
function getFieldMap(formId) {
  // console.log('Site:gravityforms:getFieldMap', formId);

  if (!(formId in formFieldMaps)) {
    throw new Error('Not found');
  }

  return formFieldMaps[formId];
}


/**
 * Populates a form's fields using the supplied data.
 *
 * This currently only works on text/hidden fields that have a single, simple string.
 *
 * @since 0.1.0
 *
 * @param {DOMElement} element The root form element.
 * @param {object}     data    The field data to use.
 *
 * ```Example data:
 * {
 *  first_name : 'Foo',
 *  phone      : '123 123-1234',
 * }
 * ```
 *
 * @throw Error
 */
export function populateForm(element, data) {
  // console.log('Site:gravityforms:populateForm', element, data);

  // The form id is part of the element id, just need to extract it
  const formId = parseInt(element.id.replace('gform_', ''), 10);
  const map = getFieldMap(formId);

  data = typeof data === 'object' ? data : {};

  // console.log('formId', formId);
  // console.log('map', map);
  // console.log('data', data);

  for (const fieldName of Object.keys(data)) {
    if (!(fieldName in map)) {
      return;
    }

    const field = map[fieldName];
    const fieldData = data[fieldName];
    const fieldElement = element.querySelector(`#input_${formId}_${field.id}`);

    // console.log(`#input_${formId}_${field.id}`);
    // console.log('field', field);
    // console.log('fieldData', fieldData);
    // console.log('fieldElement', fieldElement);

    if (!fieldElement) {
      return;
    }

    fieldElement.value = fieldData;
  }

  // Verify the form fields were filled out
  // console.log('Form fields', mapDataToFormInputs(getFormInputs(element), map));
}


/**
 * Gets all the form inputs contained by the element.
 *
 * @param {DOMElement} element The element.
 *
 * @return {NodeList} The form inputs.
 */
function getFormInputs(element) {
  // console.log('Site:gravityforms:getFormInputs', element);

  const data = {};

  element.querySelectorAll('input').forEach(function (elem) {
    data[elem.name] = elem.value;
  });

  return data;
}


/**
 * Maps field names in the NodeList from `getFormInputs` using the `map`` to give the input
 * meaningful names.
 *
 * @param {NodeList} data The data.
 * @param {object}   map  The map.
 *
 * @return {object} Field data.
 */
function mapDataToFormInputs(data, map) {
  // console.log('Site:gravityforms:mapDataToFormInputs', data, map);

  // The map is name to id, need to flip it for faster lookup
  const idToName = {};

  for (const name of Object.keys(map)) {
    idToName[map[name].id] = name;
  }

  const mappedData = {};

  for (const name of Object.keys(data)) {
    const value = data[name];
    const id = name.replace('input_', '');

    // console.log('Name:', name, id, value);

    if (id in idToName) {
      mappedData[idToName[id]] = value;
    } else {
      mappedData[name] = value;
    }
  }

  return mappedData;
}


// API
window.gravityformsSetFieldMap = setFieldMap;
